import React, { createContext, useContext, useState, useEffect } from "react";
import { onAuthStateChanged } from "firebase/auth";
import { auth, logEvent } from "../../firebase";
import { getPriceOfMenuCart, MenuCart, MenuCartItem, TemporaryMenuCartStoreType } from "../../models/fullMenuCart";
import { FullMenuCheckoutService } from "../../services/order/menuCheckout";
import { useSignedInUserState } from "../useAuthState";
import { DIFFERENT_RESTAURANT_ERROR } from "../../models/errors";
import UnavailableItemsDialog from "../../checkout/errorDialogs/ItemsNotAvailableDialog";
import MultiRestaurantErrorDialog from "../../checkout/errorDialogs/multiRestaurantErrorDialog";
import { CartService } from "../../services/functions/cartService";
import { FullMenuPageData } from "../../models/fullMenuPageData";
import { menuService } from "../../services/firebase/menuService";
import { CartServiceTransformTemporaryMenuCartToFullCartResponseType } from "../../models/CartServiceType";

// Define CartContext outside the component
const TemporaryMenuCartContext = createContext<TemporaryMenuCartContextType | null>(null);

interface TemporaryMenuCartContextType {
  temporaryMenuCart: MenuCart | null;
  loading: boolean;
  fetchTemporaryMenuCart: () => Promise<MenuCart | null>;
  updateTemporaryMenuCart: (cart: MenuCart) => Promise<void>;
  transformTemporaryMenuCartToFullCart: () => Promise<CartServiceTransformTemporaryMenuCartToFullCartResponseType | undefined>;
  removeTemporaryMenuCart: () => Promise<void>;
  removeItemFromTemporaryCart: (itemToRemove: MenuCartItem) => Promise<void>;
}

interface TemporaryMenuCartProviderProps {
  children: React.ReactNode;
}

export const TemporaryMenuCartProvider = ({ children }: TemporaryMenuCartProviderProps) => {
  const [temporaryMenuCart, setTemporaryMenuCart] = useState<MenuCart | null>(null);
  const [menuData, setMenuData] = useState<FullMenuPageData | null>(null);
  const [loading, setLoading] = useState(true);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);
  const [multiRestaurantDialogOpen, setMultiRestaurantDialogOpen] = useState(false);
  let user = useSignedInUserState();

  const fetchTemporaryMenuCart = async (): Promise<MenuCart | null> => {
    if (!user.profile) { return null }
    let fetchedCart: TemporaryMenuCartStoreType | null = null;
    setLoading(true);
    try {
      fetchedCart = await FullMenuCheckoutService.getTemporaryMenuCartCached(user.profile.id);
      setTemporaryMenuCart(fetchedCart ? fetchedCart.cart : null);
      console.log("fetchedCart", temporaryMenuCart)
      if (fetchedCart) {
        const fullMenu = await menuService.fetchFullMenuCached(fetchedCart.cart.restaurantId);
        setMenuData(fullMenu);
      }
    } catch (error) {
      console.error("Failed to fetch cart:", error);
    } finally {
      setLoading(false);
    }
    setLoading(false);
    if (fetchedCart != null) {
      return fetchedCart.cart
    }
    return null
  };

  const updateTemporaryMenuCart = async (cart: MenuCart) => {
    if (!user.profile) { return }
    try {
      console.log("cart", cart, user.profile.id)
      const response = await FullMenuCheckoutService.updateTemporaryMenuCart(cart, user.profile.id);
      console.log("response", response)
      if (response) {
        setTemporaryMenuCart(response.cart);  // Refresh cart after updating
      }
    } catch (error) {
      console.error("Failed to add item:", error);
      if ((error as Error).message == DIFFERENT_RESTAURANT_ERROR().message) {
        setMultiRestaurantDialogOpen(true);
      }
    }
  };

  const removeTemporaryMenuCart = async () => {
    if (!user.profile) { return }
    try {
      const response = await FullMenuCheckoutService.removeTemporaryMenuCart(user.profile.id);
      setTemporaryMenuCart(null);  // Refresh cart after updating
    } catch (error) {
      console.error("Failed to remove cart:", error);
    }
  };

  const removeItemFromTemporaryCart = async (itemToRemove: MenuCartItem) => {
    if (!user.profile) { return }
    const _temporaryMenuCart = await FullMenuCheckoutService.getTemporaryMenuCartCached(user.profile.id)
    if (!_temporaryMenuCart) { return }
    const _menu = await menuService.fetchFullMenuCached(_temporaryMenuCart.cart.restaurantId);
    if (!_menu) { return }
    try {
      console.log("cart", _temporaryMenuCart.cart, user.profile.id)
      console.log("old items", _temporaryMenuCart.cart.items)
      const newMenuitems: MenuCartItem[] = _temporaryMenuCart.cart.items.filter(item => item.id != itemToRemove.id)
      console.log("new items", newMenuitems)
      const newCart: MenuCart = {
        grandTotalBeforeFees: getPriceOfMenuCart(newMenuitems, _menu),
        items: newMenuitems,
        restaurantId: _temporaryMenuCart.cart.restaurantId
      }
      if (newCart.items.length == 0) {
        await removeTemporaryMenuCart();
      } else {
        console.log("new cart", newCart)
        await updateTemporaryMenuCart(newCart)
      }
    } catch (error) {
      console.error("Failed to add item:", error);
      if ((error as Error).message == DIFFERENT_RESTAURANT_ERROR().message) {
        setMultiRestaurantDialogOpen(true);
      }
    }
  }

  const transformTemporaryMenuCartToFullCart = async () => {
    console.log("profile", !user.profile, "menuCart", temporaryMenuCart)
    if (!user.profile) { return }
    try {
      const response = await CartService.transformTemporaryMenuCartToFullCart();
      return response;
    } catch (error) {
      console.error("Failed to transform cart:", error);
    }
  }

  // const checkout = async () => {
  //   try {
  //     const response: PurchaseCartResponseType = await CartService.purchaseCart();

  //     if (response.error) {
  //       setErrorDialogOpen(true);
  //       return;
  //     }
  //     if (response.unavailableItems && response.unavailableItems.length > 0) {
  //       setUnavailableItems(response.unavailableItems);
  //       setErrorDialogOpen(true);
  //       if (response.cart) {
  //         setCart(response.cart);
  //       }
  //     }
  //     if (response.yocoResponse) {
  //       logEvent(MetricsNames.ADD_PAYMENT_INFO);
  //       window.location.href = response.yocoResponse.redirectUrl;
  //     }
  //   } catch (error) {
  //     console.error("Failed to remove item:", error);
  //   }
  // };


  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      fetchTemporaryMenuCart()
    });

    return () => unsubscribe(); // Clean up the listener on unmount
  }, [auth, user.profile]);

  return <TemporaryMenuCartContext.Provider value={{
    temporaryMenuCart,
    loading,
    fetchTemporaryMenuCart,
    updateTemporaryMenuCart,
    transformTemporaryMenuCartToFullCart,
    removeTemporaryMenuCart,
    removeItemFromTemporaryCart
  }}>
    {children}

    <MultiRestaurantErrorDialog
      open={multiRestaurantDialogOpen}
      onClose={() => setMultiRestaurantDialogOpen(false)}
    />

    <UnavailableItemsDialog
      open={errorDialogOpen}
      restaurantId={temporaryMenuCart ? temporaryMenuCart.restaurantId : ""}
      onClose={() => setErrorDialogOpen(false)}
      unavailableItems={[]}
    />
  </TemporaryMenuCartContext.Provider>
};

export const useTemporaryMenuCartProviderCart = (): TemporaryMenuCartContextType => {
  const context = useContext(TemporaryMenuCartContext);
  if (!context) {
    throw new Error("useCart must be used within a TemporaryMenuCartProvider");
  }
  return context;
};
